import { createApi, fetchBaseQuery } from '@reduxjs/toolkit/query/react';
import {baseQueryWithReAuth} from "@/store/features/baseRtkQuery";

// Types matching the UI expectations
export interface BoWidgetUser {
    id: string;
    email: string;
    assignedAgent: string;
    backOfficeRole: string;
    token: string;
    isActive: boolean;
}

export interface MerchantConfig {
    merchantId: string;
    merchantName: string;
    defaultAgent: string;
    isActive: boolean;
    users: BoWidgetUser[];
}

export interface GlobalWidgetStatus {
    featureName: string;
    isActive: boolean;
    lastModified: string;
    modifiedBy: string;
    description?: string;
}

export interface UpdateGlobalStatusRequest {
    isActive: boolean;
}

export interface UpdateMerchantStatusRequest {
    merchantId: string;
    isActive: boolean;
}

export interface UpdateUserStatusRequest {
    merchantId: string;
    userId: string;
    isActive: boolean;
}

export interface UpdateMerchantDefaultAgentRequest {
    merchantId: string;
    defaultAgent: string;
}

export interface UpdateUserDefaultAgentRequest {
    userId: string;
    defaultAgent: string;
}

export interface UpdateUserLevelStatusRequest {
    userId: string;
    isActive: boolean;
}

// Backend response types (matching the new structure)
interface UserMerchantDto {
    id: string;
    userId: string;
    userEmail: string;
    userName: string;
    merchantId: string;
    merchantName: string;
    role: string;
    defaultAgent: string;
    isActive: boolean;
}

interface BackOfficeUserDto {
    userId: string;
    email: string;
    name: string;
    merchants: UserMerchantDto[];
}

const widgetBoDashboardApi = createApi({
    reducerPath: 'widgetBoDashboardApi',
    baseQuery: baseQueryWithReAuth,
    tagTypes: ['FeatureFlag', 'Merchants', 'Users'],
    endpoints: (builder) => ({
        // Get global feature flag status
        getGlobalWidgetStatus: builder.query<GlobalWidgetStatus, void>({
            query: () => '/widget-bo/feature-flag',
            providesTags: ['FeatureFlag'],
        }),

        // Update global feature flag
        updateGlobalWidgetStatus: builder.mutation<GlobalWidgetStatus, UpdateGlobalStatusRequest>({
            query: (body) => ({
                url: '/widget-bo/feature-flag',
                method: 'PUT',
                body,
            }),
            invalidatesTags: ['FeatureFlag'],
        }),

        // Get all merchants with their users (transformed from backend structure)
        getAllMerchantConfigs: builder.query<MerchantConfig[], void>({
            query: () => '/widget-bo/users',
            transformResponse: (response: BackOfficeUserDto[]) => {
                // Group user-merchant relationships by merchant
                const merchantMap = new Map<string, MerchantConfig>();

                response.forEach(user => {
                    user.merchants.forEach(userMerchant => {
                        if (!merchantMap.has(userMerchant.merchantId)) {
                            merchantMap.set(userMerchant.merchantId, {
                                merchantId: userMerchant.merchantId,
                                merchantName: userMerchant.merchantName,
                                defaultAgent: userMerchant.defaultAgent,
                                isActive: false, // Will be calculated below
                                users: []
                            });
                        }

                        const merchant = merchantMap.get(userMerchant.merchantId)!;
                        merchant.users.push({
                            id: userMerchant.id, // This is the user_merchant relationship ID
                            email: userMerchant.userEmail,
                            assignedAgent: userMerchant.defaultAgent,
                            backOfficeRole: userMerchant.role,
                            token: user.userId, // Using userId as token placeholder
                            isActive: userMerchant.isActive
                        });
                    });
                });

                // Calculate merchant isActive (true if ANY user is active)
                merchantMap.forEach(merchant => {
                    merchant.isActive = merchant.users.some(u => u.isActive);
                });

                return Array.from(merchantMap.values());
            },
            providesTags: ['Merchants', 'Users'],
        }),

        // Update merchant status (activates/deactivates all users for that merchant)
        updateMerchantStatus: builder.mutation<void, UpdateMerchantStatusRequest>({
            query: ({ merchantId, isActive }) => ({
                url: `/widget-bo/merchants/${merchantId}/status`,
                method: 'PUT',
                body: { isActive },
            }),
            invalidatesTags: ['Merchants', 'Users'],
        }),

        // Update individual user status
        updateUserStatus: builder.mutation<void, UpdateUserStatusRequest>({
            query: ({ merchantId, userId, isActive }) => ({
                url: `/widget-bo/user-merchants/${userId}/status`,
                method: 'PUT',
                body: { isActive },
            }),
            invalidatesTags: ['Users'],
        }),

        // Update merchant default agent
        updateMerchantDefaultAgent: builder.mutation<void, UpdateMerchantDefaultAgentRequest>({
            query: ({ merchantId, defaultAgent }) => ({
                url: `/widget-bo/merchants/${merchantId}/default-agent`,
                method: 'PUT',
                body: { defaultAgent },
            }),
            invalidatesTags: ['Merchants'],
        }),

        // Update user default agent (updates ALL merchants for this user)
        updateUserDefaultAgent: builder.mutation<void, UpdateUserDefaultAgentRequest>({
            query: ({ userId, defaultAgent }) => ({
                url: `/widget-bo/users/${userId}/default-agent`,
                method: 'PUT',
                body: { defaultAgent },
            }),
            invalidatesTags: ['Merchants', 'Users'],
        }),

        // Update user status (enable/disable ALL merchants for this user)
        updateUserLevelStatus: builder.mutation<void, UpdateUserLevelStatusRequest>({
            query: ({ userId, isActive }) => ({
                url: `/widget-bo/users/${userId}/status`,
                method: 'PUT',
                body: { isActive },
            }),
            invalidatesTags: ['Merchants', 'Users'],
        }),
    }),
});

export const widgetBoApiActions = {
    useGetGlobalWidgetStatusQuery: widgetBoDashboardApi.useGetGlobalWidgetStatusQuery,
    useUpdateGlobalWidgetStatusMutation: widgetBoDashboardApi.useUpdateGlobalWidgetStatusMutation,
    useGetAllMerchantConfigsQuery: widgetBoDashboardApi.useGetAllMerchantConfigsQuery,
    useUpdateMerchantStatusMutation: widgetBoDashboardApi.useUpdateMerchantStatusMutation,
    useUpdateUserStatusMutation: widgetBoDashboardApi.useUpdateUserStatusMutation, // Individual user-merchant status
    useUpdateMerchantDefaultAgentMutation: widgetBoDashboardApi.useUpdateMerchantDefaultAgentMutation,
    useUpdateUserDefaultAgentMutation: widgetBoDashboardApi.useUpdateUserDefaultAgentMutation, // User-level default agent
    useUpdateUserLevelStatusMutation: widgetBoDashboardApi.useUpdateUserLevelStatusMutation, // User-level status toggle
};

export default widgetBoDashboardApi;